/**
 * Auth Context — AuthProvider with user state, login, logout, refresh, loading states.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthContextValue, AuthState, AuthUser, LoginWithCodeResult } from './auth.types';
import {
  redirectToKeycloakLogin,
  handleKeycloakCallback,
  fetchCurrentUser,
  keycloakLogout,
  tryRestoreSession,
} from './auth.keycloak';
import { api, setOnLogoutCallback, tokenStorage } from '@/lib/api/client';
import { authEndpoints } from '@/lib/api/endpoints';
import type { AuthTokens, MfaRequiredResponse } from '@/lib/api/types';

// ============================================================
// Default state
// ============================================================

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitializing: true,
  error: null,
  mfaPendingToken: null as string | null,
  mfaSetupRequired: false,
};

// ============================================================
// Context
// ============================================================

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ============================================================
// Provider
// ============================================================

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  // ---- Initialize session on mount ----
  // Note: React 18 StrictMode in dev double-invokes effects (mount → unmount → mount).
  // We must NOT use a ref guard that prevents the second run, because the first run's
  // async operation gets cancelled by the cleanup. Instead, we allow re-initialization
  // and use the `cancelled` flag to discard stale results.
  useEffect(() => {
    let cancelled = false;

    async function init() {
      setState((s) => ({ ...s, isInitializing: true, error: null }));
      try {
        const user = await tryRestoreSession();
        if (cancelled) return;
        if (user) {
          setState({
            user: mapToAuthUser(user),
            isAuthenticated: true,
            isLoading: false,
            isInitializing: false,
            error: null,
            mfaPendingToken: null,
            mfaSetupRequired: false,
          });
        } else {
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitializing: false,
            error: null,
            mfaPendingToken: null,
            mfaSetupRequired: false,
          });
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitializing: false,
          error: err instanceof Error ? err.message : 'Failed to initialize session',
          mfaPendingToken: null,
          mfaSetupRequired: false,
        });
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Register on-logout callback so API interceptor can trigger redirect ----
  useEffect(() => {
    setOnLogoutCallback(() => {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitializing: false,
        error: null,
        mfaPendingToken: null,
        mfaSetupRequired: false,
      });
    });
  }, []);

  // ---- Login: redirect to Keycloak ----
  const login = useCallback(() => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    redirectToKeycloakLogin();
  }, []);

  // ---- Login with code: exchange auth code (used by callback page) ----
  // Returns 'mfa-required' if MFA challenge is needed, 'success' if fully authenticated.
  // The caller (AuthCallbackPage) uses the return value to decide which page to navigate to.
  const loginWithCode = useCallback(async (code: string, state?: string): Promise<LoginWithCodeResult> => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await handleKeycloakCallback(code, state || '');

      // Check if the backend requires MFA
      if (result && typeof result === 'object' && (result as MfaRequiredResponse).mfaRequired === true) {
        const mfaResponse = result as MfaRequiredResponse;
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitializing: false,
          error: null,
          mfaPendingToken: mfaResponse.mfaToken,
          mfaSetupRequired: mfaResponse.mfaSetupRequired ?? false,
        });
        return 'mfa-required';
      }

      // Normal flow: tokens returned, fetch user and authenticate
      const user = await fetchCurrentUser();
      setState({
        user: mapToAuthUser(user),
        isAuthenticated: true,
        isLoading: false,
        isInitializing: false,
        error: null,
        mfaPendingToken: null,
        mfaSetupRequired: false,
      });
      return 'success';
    } catch (err) {
      tokenStorage.clearTokens();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitializing: false,
        error: err instanceof Error ? err.message : 'Login failed',
        mfaPendingToken: null,
        mfaSetupRequired: false,
      });
      throw err;
    }
  }, []);

  // ---- Verify MFA code during login ----
  const verifyMfa = useCallback(async (code: string) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const tokens = await api.post<AuthTokens>(authEndpoints.mfaVerifyLogin, {
        mfaToken: state.mfaPendingToken,
        code,
      });

      // Store tokens
      tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);

      // Fetch user data
      const user = await fetchCurrentUser();

      setState({
        user: mapToAuthUser(user),
        isAuthenticated: true,
        isLoading: false,
        isInitializing: false,
        error: null,
        mfaPendingToken: null,
        mfaSetupRequired: false,
      });
    } catch (err) {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: err instanceof Error ? err.message : 'MFA verification failed',
      }));
      throw err;
    }
  }, [state.mfaPendingToken]);

  // ---- Cancel MFA flow ----
  // Best-effort: cancel the pending MFA session in Redis, then clean up React state.
  // Does NOT clear tokens because the user was never authenticated (no tokens stored).
  const cancelMfa = useCallback(async () => {
    if (state.mfaPendingToken) {
      try {
        await api.post(authEndpoints.mfaCancel, { mfaToken: state.mfaPendingToken });
      } catch {
        // Silent failure — we still clean up local state
      }
    }
    setState((s) => ({
      ...s,
      mfaPendingToken: null,
      mfaSetupRequired: false,
      isLoading: false,
      error: null,
    }));
  }, [state.mfaPendingToken]);

  // ---- Logout ----
  const logout = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true }));
    try {
      await keycloakLogout();
      // keycloakLogout redirects, so state may not matter, but set anyway
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitializing: false,
        error: null,
        mfaPendingToken: null,
        mfaSetupRequired: false,
      });
    } catch (err) {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Logout failed',
      }));
    }
  }, []);

  // ---- Refresh user data ----
  const refreshUser = useCallback(async () => {
    if (!tokenStorage.hasToken()) return;
    try {
      const user = await fetchCurrentUser();
      setState((s) => ({
        ...s,
        user: mapToAuthUser(user),
        isAuthenticated: true,
        error: null,
      }));
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : 'Failed to refresh user',
      }));
    }
  }, []);

  // ---- Clear error ----
  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  // ---- Memoized context value ----
  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      loginWithCode,
      logout,
      refreshUser,
      clearError,
      verifyMfa,
      cancelMfa,
    }),
    [state, login, loginWithCode, logout, refreshUser, clearError, verifyMfa, cancelMfa],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================
// Helper: map API User → AuthUser
// ============================================================

function mapToAuthUser(user: {
  id: string;
  username: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  role: string;
  preferredAlgorithm: string;
}): AuthUser {
  return {
    id: user.id,
    username: user.username,
    email: user.email,
    displayName: user.displayName,
    avatarUrl: user.avatarUrl,
    role: user.role,
    preferredAlgorithm: user.preferredAlgorithm,
  };
}